import React, { useState, useEffect, useCallback } from 'react';
import { hrEmployeesApi, type EmployeeDto } from '../../services/api';
import AddEmployeeModal, { type EmployeeFormData } from './employees/AddEmployeeModal';

export default function EmployeesPage() {
  const [employees, setEmployees] = useState<EmployeeDto[]>([]);
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(true);
  const [modalOpen, setModalOpen] = useState(false);
  const [editEmployee, setEditEmployee] = useState<EmployeeDto | null>(null);
  const [viewOnly, setViewOnly] = useState(false);

  const fetchEmployees = useCallback(async () => {
    setLoading(true);
    try {
      const res = await hrEmployeesApi.list(search || undefined);
      if (res.data) setEmployees(res.data);
    } catch {
      setEmployees([]);
    } finally {
      setLoading(false);
    }
  }, [search]);

  useEffect(() => {
    fetchEmployees();
  }, [fetchEmployees]);

  const handleSubmit = async (data: EmployeeFormData) => {
    const payload = {
      first_name: data.first_name.trim(),
      last_name: data.last_name.trim(),
      email: data.email.trim(),
      phone: data.phone.trim() || undefined,
      date_of_birth: data.date_of_birth || undefined,
      street_address: data.street_address.trim() || undefined,
      city: data.city.trim() || undefined,
      state_province: data.state_province.trim() || undefined,
      postal_code: data.postal_code.trim() || undefined,
      department: data.department.trim() || undefined,
      job_title: data.job_title.trim() || undefined,
      employment_type: data.employment_type || undefined,
      employee_type: data.employee_type || undefined,
      hire_date: data.hire_date || undefined,
      status: data.status || undefined,
      salary_type: data.salary_type || undefined,
      pay_rate: data.pay_rate ? parseFloat(data.pay_rate) : undefined,
      bank_name: data.bank_name.trim() || undefined,
      account_number: data.account_number.trim() || undefined,
      routing_number: data.routing_number.trim() || undefined,
      emergency_contact_name: data.emergency_contact_name.trim() || undefined,
      emergency_contact_relationship: data.emergency_contact_relationship.trim() || undefined,
      emergency_contact_phone: data.emergency_contact_phone.trim() || undefined,
      sss_number: data.sss_number.trim() || undefined,
      pagibig_number: data.pagibig_number.trim() || undefined,
      philhealth_number: data.philhealth_number.trim() || undefined,
      tin_number: data.tin_number.trim() || undefined,
      gsis_number: data.gsis_number.trim() || undefined,
      benefits_notes: data.benefits_notes.trim() || undefined,
      create_account: data.create_account || undefined,
      password: data.create_account ? data.password : undefined,
    };
    if (editEmployee) {
      const res = await hrEmployeesApi.update(editEmployee.id, payload);
      if (res.errors) throw res;
      await fetchEmployees();
    } else {
      const res = await hrEmployeesApi.create(payload);
      if (res.errors) throw res;
      await fetchEmployees();
    }
  };

  const openAdd = () => {
    setEditEmployee(null);
    setViewOnly(false);
    setModalOpen(true);
  };

  const openView = (emp: EmployeeDto) => {
    setEditEmployee(emp);
    setViewOnly(true);
    setModalOpen(true);
  };

  const openEdit = (emp: EmployeeDto) => {
    setEditEmployee(emp);
    setViewOnly(false);
    setModalOpen(true);
  };

  const handleDelete = async (id: string) => {
    if (!window.confirm('Delete this employee? This cannot be undone.')) return;
    try {
      await hrEmployeesApi.delete(id);
      await fetchEmployees();
    } catch {
      // show toast in real app
    }
  };

  const formatDate = (d: string | null) => (d ? new Date(d).toLocaleDateString() : '—');

  return (
    <div className="max-w-7xl mx-auto w-full flex-1 min-h-0 flex flex-col overflow-hidden">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-4 shrink-0">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Employees</h1>
          <p className="text-sm text-gray-500 mt-0.5">Manage your workforce</p>
        </div>
        <button
          type="button"
          onClick={openAdd}
          className="inline-flex items-center justify-center gap-2 px-4 py-2.5 rounded-xl bg-brand-600 text-white text-sm font-semibold hover:bg-brand-700 focus:ring-2 focus:ring-brand-500 focus:ring-offset-2 transition-colors shrink-0"
        >
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8v8H4V4h8z" />
          </svg>
          Add Employee
        </button>
      </div>

      <div className="mb-4">
        <div className="relative">
          <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400">
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
            </svg>
          </span>
          <input
            type="search"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search employees..."
            className="w-full pl-10 pr-4 py-2.5 rounded-xl border border-gray-200 text-gray-900 placeholder-gray-400 focus:ring-2 focus:ring-brand-500/20 focus:border-brand-500 outline-none"
          />
        </div>
      </div>

      <div className="bg-white rounded-xl shadow-card border border-gray-100 flex-1 min-h-0 overflow-auto">
        {loading ? (
          <div className="flex items-center justify-center py-12">
            <div className="text-gray-500 text-sm">Loading...</div>
          </div>
        ) : employees.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-12 text-center text-gray-500">
            <svg
              className="w-12 h-12 text-gray-300 mb-3"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0z"
              />
            </svg>
            <p className="font-medium text-gray-600">No employees found.</p>
            <p className="text-sm mt-1">Add your first employee to get started.</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-left">
              <thead>
                <tr className="border-b border-gray-100 bg-gray-50/80">
                  <th className="px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wider">ID</th>
                  <th className="px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wider">Name</th>
                  <th className="px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wider">Department</th>
                  <th className="px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wider">Contact</th>
                  <th className="px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wider">Hire Date</th>
                  <th className="px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wider">Type</th>
                  <th className="px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wider">Status</th>
                  <th className="px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wider w-16 text-right">Action</th>
                </tr>
              </thead>
              <tbody>
                {employees.map((emp) => (
                  <tr
                    key={emp.id}
                    className="border-b border-gray-50 hover:bg-gray-50/50 transition-colors"
                  >
                    <td className="px-4 py-3 text-sm text-gray-600">{emp.id}</td>
                    <td className="px-4 py-3 text-sm font-medium text-gray-900">{emp.full_name}</td>
                    <td className="px-4 py-3 text-sm text-gray-600">{emp.department || '—'}</td>
                    <td className="px-4 py-3 text-sm text-gray-600">
                      {emp.phone || emp.email || '—'}
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-600">{formatDate(emp.hire_date)}</td>
                    <td className="px-4 py-3 text-sm text-gray-600">{emp.employee_type ?? '—'}</td>
                    <td className="px-4 py-3">
                      <span
                        className={`inline-flex px-2 py-0.5 text-xs font-medium rounded-full ${
                          emp.status === 'Active'
                            ? 'bg-emerald-100 text-emerald-700'
                            : emp.status === 'On Leave'
                            ? 'bg-amber-100 text-amber-700'
                            : emp.status === 'Terminated'
                            ? 'bg-red-100 text-red-700'
                            : 'bg-gray-100 text-gray-700'
                        }`}
                      >
                        {emp.status}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-right">
                      <div className="inline-flex items-center gap-0.5">
                        <button
                          type="button"
                          onClick={() => openView(emp)}
                          className="p-2 rounded-lg text-gray-500 hover:bg-gray-100 hover:text-brand-600 transition-colors"
                          title="View"
                          aria-label="View employee"
                        >
                          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                          </svg>
                        </button>
                        <button
                          type="button"
                          onClick={() => openEdit(emp)}
                          className="p-2 rounded-lg text-gray-500 hover:bg-gray-100 hover:text-brand-600 transition-colors"
                          title="Edit"
                          aria-label="Edit employee"
                        >
                          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                          </svg>
                        </button>
                        <button
                          type="button"
                          onClick={() => handleDelete(emp.id)}
                          className="p-2 rounded-lg text-gray-500 hover:bg-gray-100 hover:text-red-600 transition-colors"
                          title="Delete"
                          aria-label="Delete employee"
                        >
                          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                          </svg>
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <AddEmployeeModal
        open={modalOpen}
        onClose={() => { setModalOpen(false); setViewOnly(false); }}
        onSubmit={handleSubmit}
        editEmployee={editEmployee}
        viewOnly={viewOnly}
      />
    </div>
  );
}
